import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { filter, finalize, map, takeUntil } from 'rxjs/operators';
import { DialItem, DialItemClickEvent } from '@progress/kendo-angular-buttons';
import {
    commentIcon,
    gearsIcon,
    pencilIcon,
    fileAddIcon,
    checkCircleIcon,
    plusIcon,
    SVGIcon
} from '@progress/kendo-svg-icons';
import { SelectEvent } from '@progress/kendo-angular-layout';

import { IORD_Order } from '../../shared/interfaces/request.interface';
import { MaterialMasterPurchasePartRequest } from '../../shared/interfaces/request.interface';
import { RequestActions } from '../../shared/store/action/request.action';
import { RequestState } from '../../shared/store/state/request.state';
import { AccountActions } from '../../shared/store/action/account.action';
import { AccountState } from '../../shared/store/state/account.state';
import { OptionActions } from '../../shared/store/action/options.action';
import { UserViewModel } from '../../shared/interfaces/viewmodels/userviewmodel';
import { FileVaultApiService } from '../../shared/services/filevault-api.service';
import { GlobalNotificationService } from '../../shared/services/globalnotification.service';
import { RTAApiClient } from '../../shared/services/rta-api.service';
import { ChangeDetailEvent } from '@filevault/components';

declare var CURRENT_USER_ID: number;
declare var CURRENT_USER_LOGINID: string;
declare var CURRENT_USER_DISPLAYNAME: string;

@Component({
    selector: 'app-irta',
    templateUrl: './irta.component.html',
    styleUrls: ['./irta.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class IrtaComponent implements OnInit, OnDestroy {

    public id!: string;
    public isCreateMode = false;
    public request$!: Observable<IORD_Order | undefined>;
    public user$!: Observable<UserViewModel>;
    private destroy$ = new Subject<void>();

    // ─── Edit Mode Fields ───
    public isLoading = true;
    public selectedTabIndex = 0;

    // FileVault part data
    public partId: number | null = null;
    public partNumber: string = '';
    public materialNumber: string = '';
    public isLoadingPart = false;

    // FileVault change data
    public changeId: string | null = null;

    // Drawing number dialog
    public showDrawingDialog = false;
    public drawingSubmitting = false;

    // Change order dialog
    public showChangeDialog = false;
    public changeReason: string = '';
    public changeDescription: string = '';
    public isSubmittingChange = false;

    // Chat
    public viewChat = false;
    public windowLeft = 50;

    // File list refresh
    public fileListRefreshToken = 0;

    // Icons
    public gearsIcon: SVGIcon = gearsIcon;
    public commentIcon: SVGIcon = commentIcon;
    public pencilIcon: SVGIcon = pencilIcon;
    public fileAddIcon: SVGIcon = fileAddIcon;
    public checkCircleIcon: SVGIcon = checkCircleIcon;
    public plusIcon: SVGIcon = plusIcon;

    public currentUserId: number = CURRENT_USER_ID;
    public currentUserLoginId: string = CURRENT_USER_LOGINID;
    public actionItems: Array<DialItem> = [];

    // ─── Part Number Request ──────────────────────────────────
    public showPartNumberDialog = false;
    public isSubmittingPartNumbers = false;
    public partNumberRequests: MaterialMasterPurchasePartRequest[] = [];
    public csvDragOver = false;
    public csvParseErrors: string[] = [];

    // Dropdown options for the form
    public purchasePartOptions = [
        { text: 'Cutter', value: 2 },
        { text: 'US', value: 3 },
        { text: 'CutterDisp', value: 6 },
        { text: 'Generic', value: 7 },
        { text: 'HardfacingDisp', value: 8 }
    ];
    public procurementTypeOptions = [
        { text: 'F', value: 1 },
        { text: 'X', value: 2 },
        { text: 'E', value: 3 }
    ];
    public materialTypeOptions = [
        { text: 'ZFSP', value: 1 },
        { text: 'ZRMT', value: 2 },
        { text: 'ZSER', value: 3 }
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private store: Store,
        private fileVaultApi: FileVaultApiService,
        private notificationService: GlobalNotificationService,
        private rtaApiClient: RTAApiClient
    ) { }

    ngOnInit(): void {
        this.user$ = this.store.select(AccountState.user);
        this.buildActionItems();
        this.route.paramMap.pipe(
            takeUntil(this.destroy$),
            map((params: ParamMap) => params.get('id'))
        ).subscribe(idParam => {
            if (idParam) {
                // ── Edit Mode ──
                this.isCreateMode = false;
                this.id = idParam;
                this.changeId = idParam;
                this.initEditMode(idParam);
            } else {
                // ── Create Mode ──
                this.isCreateMode = true;
                this.isLoading = false;
                this.store.dispatch(new AccountActions.GetAllUserDetails());
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ═══════════════════════════════════════════════════════════
    // CREATE MODE
    // ═══════════════════════════════════════════════════════════

    /**
     * Called when fv-change-create emits a saved change object.
     * Navigates to the edit view or shows a success message.
     */
    public onChangeSaved(change: any): void {
        if (change?.id) {
            this.notificationService.showSuccessNotification(
                `Change "${change.title || change.id}" created successfully`
            );
            // Navigate to iRTA edit mode with the new change ID
            this.router.navigate(['/irta', change.id]);
        } else {
            this.notificationService.showSuccessNotification('Change created successfully');
        }
    }

    /**
     * Called when fv-change-create cancel is clicked.
     * Navigates back to dashboard.
     */
    public onChangeCancelled(): void {
        this.router.navigate(['/dashboard']);
    }

    // ═══════════════════════════════════════════════════════════
    // EDIT MODE
    // ═══════════════════════════════════════════════════════════

    private initEditMode(id: string): void {
        this.store.dispatch(new AccountActions.GetAllUserDetails());

        this.isLoading = false;
    }

    // ─── FileVault Part Resolution ────────────────────────────
    private resolveFileVaultPart(request: IORD_Order): void {
        const materialNum = this.getMaterialNumber(request);
        if (!materialNum || materialNum === 'Pending') {
            this.partId = null;
            this.partNumber = '';
            return;
        }
        this.isLoadingPart = true;
        this.fileVaultApi.getPartIdByPartNumber(materialNum).pipe(
            takeUntil(this.destroy$),
            finalize(() => this.isLoadingPart = false)
        ).subscribe({
            next: (response) => {
                if (response?.id) {
                    this.partId = response.id;
                    this.partNumber = materialNum;
                }
            },
            error: () => {
                this.partId = null;
                this.partNumber = '';
            }
        });
    }

    // ─── FAB Action Items ─────────────────────────────────────
    private buildActionItems(): void {
        this.actionItems = [
            { svgIcon: this.pencilIcon, label: 'Request Drawing Number' },
            { svgIcon: this.fileAddIcon, label: 'Create Change Order' },
            { svgIcon: this.plusIcon, label: 'Request Part Numbers' },
            { svgIcon: this.commentIcon, label: 'Open Chat' }
        ];
    }

    public onDialItemClick(e: DialItemClickEvent): void {
        switch (e.item.label) {
            case 'Request Drawing Number':
                this.showDrawingDialog = true;
                break;
            case 'Request Part Numbers':
                this.openPartNumberDialog();
                break;
        }
    }

    // ─── Tab Selection ────────────────────────────────────────
    public onTabSelect(e: SelectEvent): void {
        this.selectedTabIndex = e.index;
    }

    // ─── Change Order Dialog ──────────────────────────────────
    public openChangeDialog(): void {
        this.changeReason = '';
        this.changeDescription = '';
        this.showChangeDialog = true;
    }

    public closeChangeDialog(): void {
        this.showChangeDialog = false;
    }

    public submitChangeOrder(): void {
        if (!this.changeReason?.trim()) {
            this.notificationService.showWarningNotification('Please select a reason for the change.');
            return;
        }
        this.isSubmittingChange = true;
        setTimeout(() => {
            this.isSubmittingChange = false;
            this.notificationService.showSuccessNotification('Change order initiated for iRTA #' + this.id);
            this.closeChangeDialog();
            this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.id }));
        }, 1000);
    }

    // ─── Drawing Number Dialog ────────────────────────────────
    public closeDrawingDialog(): void {
        this.showDrawingDialog = false;
    }

    // ─── File Upload ──────────────────────────────────────────
    public onUploadCompleted(_item: any): void {
        this.fileListRefreshToken++;
    }

    // ─── Lifecycle Events ─────────────────────────────────────
    public onLifecycleTransition(_event: any): void {
        this.notificationService.showSuccessNotification('Lifecycle state updated');
        this.store.dispatch(new RequestActions.GetRequestDetails({ id: this.id }));
    }

    public onBomChanged(_event: any): void {
        this.notificationService.showSuccessNotification('BOM updated successfully');
    }

    // ─── Affected Items Events ────────────────────────────────
    public onAffectedItemsChanged(_event: any): void {
        this.notificationService.showSuccessNotification('Affected items updated');
    }

    // ─── Helpers ──────────────────────────────────────────────
    public getMaterialNumber(request: IORD_Order): string {
        if (!request?.sapNumbers?.length) return 'Pending';
        const materialNum = request.sapNumbers.find(s => s.sapNumTypeId === 3);
        return materialNum?.number?.toString() ?? 'Pending';
    }

    public copyToClipboard(text: string, label: string): void {
        if (!text || text === 'Pending') {
            this.notificationService.showInfoNotification(`No ${label} available to copy`);
            return;
        }
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                this.notificationService.showSuccessNotification(`${label} copied to clipboard`);
            }).catch(() => this.fallbackCopy(text, label));
        } else {
            this.fallbackCopy(text, label);
        }
    }

    private fallbackCopy(text: string, label: string): void {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
            document.execCommand('copy');
            this.notificationService.showSuccessNotification(`${label} copied to clipboard`);
        } catch {
            this.notificationService.showErrorNotification(`Failed to copy ${label}`);
        }
        document.body.removeChild(ta);
    }
    onChangeStateChanged(event: ChangeDetailEvent) {
        switch (event.action) {
            case 'auto-converted':
                // ECR was approved and auto-converted to ECO — navigate to the new ECO
                this.router.navigate(['/irta', event.autoConvertedEcoId]);
                // Optionally show a toast:
                // this.toast.info(`ECR approved. ECO ${event.autoConvertedEcoNumber} created.`);
                break;
            case 'converted':
                // Manual convert-to-ECO — navigate to the new ECO
                this.router.navigate(['/irta', event.change.id]);
                break;
            case 'approved':
                // Normal approval (no auto-conversion) — stay on page
                break;
        }

    }
    onSignoffComplete(event: any) {    // Handle individual signoff completion if needed
        console.log('Signoff step completed:', event);
    }
    onAllSignoffsComplete(event: any) {    // Handle entire workflow completion if needed
        console.log('All signoff steps completed:', event);
    }

    // ═══════════════════════════════════════════════════════════
    // PART NUMBER REQUEST
    // ═══════════════════════════════════════════════════════════

    public openPartNumberDialog(): void {
        this.partNumberRequests = [];
        this.csvParseErrors = [];
        this.addEmptyPartRow();
        this.showPartNumberDialog = true;
    }

    public closePartNumberDialog(): void {
        this.showPartNumberDialog = false;
        this.partNumberRequests = [];
        this.csvParseErrors = [];
    }

    public addEmptyPartRow(): void {
        this.partNumberRequests.push(new MaterialMasterPurchasePartRequest({
            materialWeight: '',
            componentDescription: '',
            mrp: '007',
            oldMaterial: '',
            purchasePart: 7,
            procurementType: 1,
            requestTargetId: this.id ? parseInt(this.id, 10) || undefined : undefined,
            materialType: 3,
            unitofMeasure: 'EA',
            productHierarchy: ''
        }));
    }

    public removePartRow(index: number): void {
        this.partNumberRequests.splice(index, 1);
    }

    // ─── CSV Drag & Drop / File Select ───────────────────────
    public onCsvDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.csvDragOver = true;
    }

    public onCsvDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.csvDragOver = false;
    }

    public onCsvDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.csvDragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.processCsvFile(files[0]);
        }
    }

    public onCsvFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.processCsvFile(input.files[0]);
            input.value = ''; // reset so same file can be re-selected
        }
    }

    private processCsvFile(file: File): void {
        if (!file.name.toLowerCase().endsWith('.csv')) {
            this.notificationService.showWarningNotification('Please select a CSV file.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            const text = e.target?.result as string;
            if (text) {
                this.parseCsv(text);
            }
        };
        reader.onerror = () => {
            this.notificationService.showErrorNotification('Failed to read the CSV file.');
        };
        reader.readAsText(file);
    }

    private parseCsv(csvText: string): void {
        this.csvParseErrors = [];
        const lines = csvText.split(/\r?\n/).filter(l => l.trim().length > 0);

        if (lines.length === 0) {
            this.csvParseErrors.push('CSV file is empty.');
            return;
        }

        // Check if first line is a header row
        const expectedHeaders = [
            'materialweight', 'componentdescription', 'mrp', 'oldmaterial',
            'purchasepart', 'procurementtype', 'requesttargetid', 'materialtype',
            'unitofmeasure', 'producthierarchy'
        ];
        const firstLineLower = lines[0].toLowerCase().replace(/"/g, '');
        const firstFields = this.parseCsvLine(firstLineLower);
        const isHeader = firstFields.some(f => expectedHeaders.includes(f.trim()));
        const dataStartIndex = isHeader ? 1 : 0;

        let addedCount = 0;
        for (let i = dataStartIndex; i < lines.length; i++) {
            const fields = this.parseCsvLine(lines[i]);
            if (fields.length < 10) {
                this.csvParseErrors.push(`Row ${i + 1}: Expected 10 columns but found ${fields.length}.`);
                continue;
            }

            const desc = fields[1]?.trim();
            if (desc && desc.length > 40) {
                this.csvParseErrors.push(`Row ${i + 1}: Description "${desc.substring(0, 20)}..." exceeds 40 characters (${desc.length}).`);
            }

            const prodHierarchy = fields[9]?.trim();
            if (prodHierarchy && !prodHierarchy.startsWith('18')) {
                this.csvParseErrors.push(`Row ${i + 1}: ProductHierarchy "${prodHierarchy}" must start with "18".`);
            }

            const req = new MaterialMasterPurchasePartRequest({
                materialWeight: fields[0]?.trim(),
                componentDescription: desc,
                mrp: fields[2]?.trim(),
                oldMaterial: fields[3]?.trim(),
                purchasePart: parseInt(fields[4]?.trim(), 10) || 7,
                procurementType: parseInt(fields[5]?.trim(), 10) || 1,
                requestTargetId: parseInt(fields[6]?.trim(), 10) || undefined,
                materialType: parseInt(fields[7]?.trim(), 10) || 3,
                unitofMeasure: fields[8]?.trim() || 'EA',
                productHierarchy: prodHierarchy
            });
            this.partNumberRequests.push(req);
            addedCount++;
        }

        if (addedCount > 0) {
            this.notificationService.showSuccessNotification(`${addedCount} row(s) imported from CSV.`);
        }
    }

    /** Parse a single CSV line, respecting quoted fields with commas inside */
    private parseCsvLine(line: string): string[] {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"') {
                    if (i + 1 < line.length && line[i + 1] === '"') {
                        current += '"';
                        i++; // skip escaped quote
                    } else {
                        inQuotes = false;
                    }
                } else {
                    current += ch;
                }
            } else {
                if (ch === '"') {
                    inQuotes = true;
                } else if (ch === ',') {
                    result.push(current);
                    current = '';
                } else {
                    current += ch;
                }
            }
        }
        result.push(current);
        return result;
    }

    // ─── Validation ──────────────────────────────────────────
    public validatePartRequests(): string[] {
        const errors: string[] = [];
        this.partNumberRequests.forEach((req, i) => {
            const row = i + 1;
            if (!req.componentDescription?.trim()) {
                errors.push(`Row ${row}: Description is required.`);
            } else if (req.componentDescription.length > 40) {
                errors.push(`Row ${row}: Description must be ≤ 40 characters.`);
            }
            if (!req.productHierarchy?.trim()) {
                errors.push(`Row ${row}: Product Hierarchy is required.`);
            } else if (!req.productHierarchy.startsWith('18')) {
                errors.push(`Row ${row}: Product Hierarchy must start with "18".`);
            }
            if (!req.materialWeight?.trim()) {
                errors.push(`Row ${row}: Material Weight is required.`);
            }
        });
        return errors;
    }

    // ─── Submit ──────────────────────────────────────────────
    public submitPartNumberRequest(): void {
        if (this.partNumberRequests.length === 0) {
            this.notificationService.showWarningNotification('Add at least one part number request.');
            return;
        }

        const validationErrors = this.validatePartRequests();
        if (validationErrors.length > 0) {
            this.notificationService.showErrorNotification(validationErrors[0]);
            return;
        }

        // Stamp userId on each request
        this.partNumberRequests.forEach(r => {
            r.userId = this.currentUserId;
        });

        this.isSubmittingPartNumbers = true;
        this.rtaApiClient.fvCreateMassPartNumbers(this.partNumberRequests, this.id).pipe(
            takeUntil(this.destroy$),
            finalize(() => this.isSubmittingPartNumbers = false)
        ).subscribe({
            next: (results) => {
                const successCount = results?.length ?? 0;
                this.notificationService.showSuccessNotification(
                    `${successCount} part number(s) created successfully.`
                );
                this.closePartNumberDialog();
            },
            error: (err) => {
                console.error('Part number creation failed', err);
                this.notificationService.showErrorNotification(
                    err?.message || 'Failed to create part numbers. Please try again.'
                );
            }
        });
    }
}
